/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { getTasks } from 'store/modules/ai_management_store/actions';
import { formatDateTimeLong } from 'services/ai_management_dates';
import {
  BiddingTaskChecklistItem,
  BiddingTaskComment,
  BiddingTaskTypes,
} from 'store/modules/bidding_store/types';
import {
  addChecklistItem,
  addTaskComment,
  deleteChecklistItem,
  deleteTaskComment,
  updateChecklistItem,
  updateTask,
  UpdateTaskPayload,
} from 'services/actions/ai_management_tasks';
import AiManagementDatetimePicker from 'components/atoms/ai_management_datetime_picker';
import CommentInput from 'components/molecules/comment_input';
import AiManagementChecklist from 'components/molecules/ai_management_checklist';
import { Container } from './styles';

export interface TaskDraft {
  title: string;
  responsibleId: number | null;
  deadline: string;
  description: string;
  done: boolean;
}

const toDraft = (task: BiddingTaskTypes): TaskDraft => ({
  title: task.title,
  responsibleId: task.responsible?.id ?? null,
  deadline: task.deadline ? moment.parseZone(task.deadline).format('YYYY-MM-DDTHH:mm') : '',
  description: task.description ?? '',
  done: Boolean(task.finished_at) || task.status === 'done',
});

const toIsoDeadline = (value: string) => (value.length === 16 ? `${value}:00` : value);

interface AiManagementTaskDetailProps {
  task: BiddingTaskTypes;
  onSaved: (task: BiddingTaskTypes) => void;
}

const AiManagementTaskDetail: React.FC<AiManagementTaskDetailProps> = ({ task, onSaved }) => {
  const dispatch = useDispatch();
  const { team, taskColumns } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const [draft, setDraft] = useState<TaskDraft>(() => toDraft(task));
  const [initialDraft, setInitialDraft] = useState<TaskDraft>(() => toDraft(task));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checklistItems, setChecklistItems] = useState<BiddingTaskChecklistItem[]>(
    () => task.checklist_items ?? [],
  );
  const [comments, setComments] = useState<BiddingTaskComment[]>(() => task.comments ?? []);

  useEffect(() => {
    setDraft(toDraft(task));
    setInitialDraft(toDraft(task));
    setChecklistItems(task.checklist_items ?? []);
    setComments(task.comments ?? []);
    setError(null);
  }, [task]);

  useEffect(() => {
    if (!taskColumns.length) dispatch(getTasks());
  }, [dispatch, taskColumns.length]);

  const doneColumnId = taskColumns.find((column) => column.system_key === 'done')?.id;
  const todoColumnId = taskColumns.find((column) => column.system_key === 'todo')?.id;

  const patchDraft = (values: Partial<TaskDraft>) =>
    setDraft((current) => ({ ...current, ...values }));

  const handleSave = () => {
    const payload: UpdateTaskPayload = {};
    if (draft.title.trim() !== initialDraft.title) payload.title = draft.title.trim();
    if (draft.description !== initialDraft.description)
      payload.description = draft.description.trim() || null;
    if (draft.deadline !== initialDraft.deadline)
      payload.deadline = draft.deadline ? toIsoDeadline(draft.deadline) : null;
    if (draft.responsibleId !== initialDraft.responsibleId)
      payload.responsible_id = draft.responsibleId;
    if (draft.done !== initialDraft.done) {
      const targetColumn = draft.done ? doneColumnId : todoColumnId;
      if (targetColumn) payload.task_column_id = targetColumn;
    }

    if (!Object.keys(payload).length) return;

    setSaving(true);
    setError(null);
    updateTask(task.id, payload)
      .then((res) => {
        const updated = res?.data?.task;
        if (!res || res.status >= 400 || !updated?.id) {
          setError(
            res?.data?.errors?.[0] ?? res?.data?.error ?? 'Não foi possível salvar a tarefa.',
          );
          return;
        }
        onSaved(updated);
      })
      .finally(() => setSaving(false));
  };

  const responsibleOptions =
    task.responsible && !team.some((member) => member.id === task.responsible?.id)
      ? [{ id: task.responsible.id, name: task.responsible.name }, ...team]
      : team;

  const attachments = task.attachments ?? [];

  const orderedChecklist = [...checklistItems].sort((a, b) => a.position - b.position);
  const itemsLoaded = task.checklist_items !== undefined;
  const checklistTotal = itemsLoaded ? orderedChecklist.length : task.checklist.total;
  const checklistDone = itemsLoaded
    ? orderedChecklist.filter((item) => item.done).length
    : task.checklist.done;

  const handleAddChecklistItem = (content: string) => {
    addChecklistItem(task.id, content).then((res) => {
      const created = res?.data?.checklist_item;
      if (!res || res.status >= 400 || !created?.id) return;
      setChecklistItems((items) => [...items, created]);
    });
  };

  const handleToggleChecklistItem = (key: string) => {
    const itemId = Number(key);
    const current = checklistItems.find((item) => item.id === itemId);
    if (!current) return;

    const done = !current.done;
    setChecklistItems((items) =>
      items.map((item) => (item.id === itemId ? { ...item, done } : item)),
    );
    updateChecklistItem(task.id, itemId, { done }).then((res) => {
      if (res && res.status < 400) return;
      setChecklistItems((items) =>
        items.map((item) => (item.id === itemId ? { ...item, done: !done } : item)),
      );
    });
  };

  const handleRemoveChecklistItem = (key: string) => {
    const itemId = Number(key);
    deleteChecklistItem(task.id, itemId).then((res) => {
      if (!res || res.status >= 400) return;
      setChecklistItems((items) => items.filter((item) => item.id !== itemId));
    });
  };

  const handleAddComment = (text: string) => {
    if (!text.trim()) return;
    addTaskComment(task.id, text.trim()).then((res) => {
      const created = res?.data?.comment;
      if (!res || res.status >= 400 || !created?.id) return;
      setComments((items) => [...items, created]);
    });
  };

  const handleDeleteComment = (commentId: number) => {
    if (!window.confirm('Excluir esta anotação?')) return;
    deleteTaskComment(task.id, commentId).then((res) => {
      if (!res || res.status >= 400) return;
      setComments((items) => items.filter((item) => item.id !== commentId));
    });
  };

  return (
    <Container className="d-flex flex-column">
      <div className="task-detail-body px-4 pt-4 pb-4">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="task-title">Nome da tarefa</label>
          <input
            id="task-title"
            type="text"
            className="form-control"
            value={draft.title}
            onChange={(event) => patchDraft({ title: event.target.value })}
          />
        </div>

        <div className="form-row">
          <div className="form-group col-md-7">
            <label htmlFor="task-responsible">Responsável</label>
            <select
              id="task-responsible"
              className="form-control"
              value={draft.responsibleId ?? ''}
              onChange={(event) =>
                patchDraft({
                  responsibleId: event.target.value ? Number(event.target.value) : null,
                })
              }
            >
              <option value="">Sem responsável</option>
              {responsibleOptions.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="task-deadline">Prazo</label>
            <AiManagementDatetimePicker
              id="task-deadline"
              value={draft.deadline}
              onChange={(deadline) => patchDraft({ deadline })}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="task-description">Descrição</label>
          <textarea
            id="task-description"
            className="form-control"
            rows={4}
            placeholder="Digite uma descrição para a tarefa"
            value={draft.description}
            onChange={(event) => patchDraft({ description: event.target.value })}
          />
        </div>

        <div className="task-section-title">Anexos</div>
        <div className="card border">
          <table className="table mb-0">
            <thead className="thead-light">
              <tr>
                <th scope="col">Nome do arquivo</th>
                <th scope="col">Data</th>
                <th scope="col" aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {attachments.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted text-center py-3">
                    Nenhum anexo nesta tarefa.
                  </td>
                </tr>
              )}
              {attachments.map((attachment) => (
                <tr key={attachment.id}>
                  <td className="align-middle">{attachment.filename}</td>
                  <td className="align-middle text-nowrap">
                    {formatDateTimeLong(attachment.created_at)}
                  </td>
                  <td className="task-attachment-actions align-middle text-right text-nowrap">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm mr-1"
                      aria-label={`Excluir ${attachment.filename}`}
                    >
                      <i className="material-icons">delete_outline</i>
                    </button>
                    <a
                      className="btn btn-outline-secondary btn-sm"
                      href={`${process.env.REACT_APP_API_ENDPOINT}${attachment.url}`}
                      download={attachment.filename}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Baixar ${attachment.filename}`}
                    >
                      <i className="material-icons">file_download</i>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="task-section-title d-flex align-items-center justify-content-between">
          Checklist
          <span className="text-muted small font-weight-normal">
            {checklistDone} de {checklistTotal}
          </span>
        </div>
        <AiManagementChecklist
          items={orderedChecklist.map((item) => ({
            key: String(item.id),
            content: item.content,
            done: item.done,
          }))}
          total={checklistTotal}
          done={checklistDone}
          onAdd={handleAddChecklistItem}
          onToggle={handleToggleChecklistItem}
          onRemove={handleRemoveChecklistItem}
        />

        <div className="task-section-title">Anotações</div>
        {comments.length === 0 && (
          <div className="text-muted mb-2">Nenhuma anotação nesta tarefa.</div>
        )}
        {comments.map((comment) => (
          <div key={comment.id} className="task-note rounded p-3 mb-2">
            <div className="d-flex justify-content-between align-items-baseline mb-1">
              <span className="task-note-author font-weight-bold text-truncate">
                {comment.author?.name}
              </span>
              <span className="d-flex align-items-center text-nowrap ml-2">
                <span className="text-secondary small">
                  {formatDateTimeLong(comment.created_at)}
                </span>
                <button
                  type="button"
                  className="task-note-delete btn btn-link p-0 ml-2"
                  onClick={() => handleDeleteComment(comment.id)}
                >
                  Excluir
                </button>
              </span>
            </div>
            <div>{comment.text}</div>
          </div>
        ))}
        <div className="mt-3">
          <CommentInput
            isLoading={false}
            createComment={handleAddComment}
            commentSource="ai_management"
          />
        </div>
      </div>

      <div className="task-detail-footer d-flex align-items-center justify-content-between border-top px-4 py-3">
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input"
            id="task-done"
            checked={draft.done}
            onChange={(event) => patchDraft({ done: event.target.checked })}
          />
          <label className="custom-control-label" htmlFor="task-done">
            Marcar tarefa como concluída
          </label>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !draft.title.trim()}
        >
          {saving ? 'Salvando...' : 'Salvar tarefa'}
        </button>
      </div>
    </Container>
  );
};

export default AiManagementTaskDetail;
